// valuePass/admin/src/api/client.ts
//
// Typed REST client for the ValuePass Cloud Functions API.
//
// ── Design ────────────────────────────────────────────────────────────────────
//
//   All write operations (create, update, delete) go through this client so
//   that business-logic validation is always enforced server-side. Read
//   operations for real-time data (analytics, voucher ledger) use Firestore
//   listeners directly via the hooks in src/hooks/.
//
//   Every function:
//     • Accepts typed request parameters
//     • Returns a typed response or throws an ApiError
//     • Never swallows errors — callers decide how to handle them
//
//   Base URL resolution:
//     • In production (Firebase Hosting) /api/* is rewritten to the Cloud
//       Function by firebase.json's hosting.rewrites config.
//     • In development the Vite proxy in vite.config.ts forwards /api/* to
//       the local Firebase Emulator.
//     • VITE_API_BASE_URL can override both when set in .env.local.
//
// ── Error handling ────────────────────────────────────────────────────────────
//
//   The Cloud Functions API returns errors in the shape:
//     { error: string, code?: string, violations?: Violation[] }
//
//   This client wraps them in an ApiError instance so callers can
//   check instanceof ApiError and read .code / .violations.
//
// ─────────────────────────────────────────────────────────────────────────────

import type {
  Beneficiary,
  BeneficiaryStatus,
  Campaign,
  CampaignStatus,
  CampaignRules,
  Voucher,
  CampaignAnalytics,
  DailySnapshot,
} from "@/types";

// ── Base URL ──────────────────────────────────────────────────────────────────

const BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

// ── ApiError ──────────────────────────────────────────────────────────────────

export interface ApiViolation {
  code:    string;
  message: string;
  field:   string | null;
}

/**
 * Thrown when the server returns a non-2xx response.
 * Carries the machine-readable `code` and the full `violations` array
 * so UI components can render per-field error messages.
 */
export class ApiError extends Error {
  readonly status:     number;
  readonly code:       string;
  readonly violations: ApiViolation[];

  constructor(
    message: string,
    status:     number,
    code:       string,
    violations: ApiViolation[] = [],
  ) {
    super(message);
    this.name       = "ApiError";
    this.status     = status;
    this.code       = code;
    this.violations = violations;
  }

  /** Returns true when this is a duplicate-request (idempotency replay). */
  get isDuplicate(): boolean {
    return this.code === "DUPLICATE_REQUEST";
  }

  /** Returns true when the voucher has already been fully redeemed. */
  get isAlreadyClosed(): boolean {
    return this.code === "ALREADY_CLOSED";
  }

  /** Returns true when campaign rules were violated. */
  get isRulesViolation(): boolean {
    return this.violations.length > 0;
  }
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────

/**
 * Wraps `fetch` with:
 *   • JSON request/response serialisation
 *   • ApiError construction on non-2xx responses
 *   • Auth token injection (Firebase ID token)
 *
 * @internal
 */
async function apiFetch<T>(
  path:    string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  const { token, ...fetchOpts } = options;

  const headers = new Headers({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(fetchOpts.headers as Record<string, string> | undefined),
  });

  const res = await fetch(`${BASE}${path}`, {
    ...fetchOpts,
    headers,
  });

  // 204 No Content — nothing to parse
  if (res.status === 204) return undefined as T;

  let body: Record<string, unknown>;
  try {
    body = await res.json();
  } catch {
    throw new ApiError(
      `Server returned an unparseable response (HTTP ${res.status}).`,
      res.status,
      "PARSE_ERROR",
    );
  }

  if (!res.ok) {
    throw new ApiError(
      (body.error as string) ?? `Request failed with HTTP ${res.status}.`,
      res.status,
      (body.code as string) ?? "API_ERROR",
      (body.violations as ApiViolation[]) ?? [],
    );
  }

  return body as T;
}

// ── Auth token helper ─────────────────────────────────────────────────────────
//
// Lazily imports Firebase Auth to avoid a circular dependency with firebase.ts.
// Returns null when the user is not signed in (the server will reject with 401).

async function getIdToken(): Promise<string | null> {
  try {
    const { getFirebaseAuth } = await import("@/lib/firebase");
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    return user ? user.getIdToken() : null;
  } catch {
    return null;
  }
}

/**
 * Authenticated fetch — automatically attaches the current user's Firebase
 * ID token. Use this for all admin write operations.
 */
async function authFetch<T>(
  path:    string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getIdToken();
  return apiFetch<T>(path, { ...options, token: token ?? undefined });
}

// ── Pagination helpers ────────────────────────────────────────────────────────

export interface Page<T> {
  data:    T[];
  hasMore: boolean;
  lastId:  string | null;
  count:   number;
}

function buildQuery(params: object): string {
  const entries = Object.entries(params as Record<string, unknown>).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  );
  return `?${qs.toString()}`;
}

// ── Campaigns ─────────────────────────────────────────────────────────────────

export interface ListCampaignsParams {
  status?:     CampaignStatus;
  issuerId?:   string;
  limit?:      number;
  startAfter?: string;
}

export interface CreateCampaignBody {
  name:                  string;
  description?:          string;
  issuerId?:             string;
  startDate:             string;
  endDate:               string;
  budgetCap?:            number | null;
  defaultVoucherBalance?: number;
  rules?:                Partial<CampaignRules>;
}

export const campaignsApi = {
  /** Lists campaigns with optional status / issuer filter, paginated. */
  list: (params: ListCampaignsParams = {}): Promise<{ campaigns: Campaign[]; hasMore: boolean; lastId: string | null }> =>
    apiFetch(`/campaigns${buildQuery(params)}`),

  /** Fetches a single campaign by ID. */
  get: (id: string): Promise<{ campaign: Campaign }> =>
    apiFetch(`/campaigns/${id}`),

  /** Creates a new campaign in DRAFT status. */
  create: (body: CreateCampaignBody): Promise<{ success: boolean; campaign: Campaign }> =>
    authFetch("/campaigns", {
      method: "POST",
      body:   JSON.stringify(body),
    }),

  /** Transitions a campaign to a new status (ACTIVE, PAUSED, CLOSED, etc.). */
  setStatus: (
    id:     string,
    status: CampaignStatus,
  ): Promise<{ success: boolean; status: CampaignStatus }> =>
    authFetch(`/campaigns/${id}/status`, {
      method: "PATCH",
      body:   JSON.stringify({ status }),
    }),
};

// ── Vouchers ──────────────────────────────────────────────────────────────────

export interface ListVouchersParams {
  campaignId?:    string;
  beneficiaryId?: string;
  voucherType?:   "ORIGINAL" | "CONTINUATION";
  limit?:         number;
  startAfter?:    string;
}

export interface CreateVoucherBody {
  balance:        number;
  campaignId?:    string | null;
  beneficiaryId?: string | null;
  issuerId?:      string | null;
}

export const vouchersApi = {
  /** Lists all vouchers with optional filters, newest-first, paginated. */
  list: (params: ListVouchersParams = {}): Promise<{ vouchers: Voucher[] }> =>
    apiFetch(`/vouchers${buildQuery(params)}`),

  /** Fetches a single voucher by its 16-character number. */
  get: (voucherNumber: string): Promise<Voucher> =>
    apiFetch(`/voucher/${encodeURIComponent(voucherNumber)}`),

  /** Issues a new ORIGINAL voucher, optionally linked to a campaign/beneficiary. */
  create: (body: CreateVoucherBody): Promise<{ success: boolean; voucher: Voucher }> =>
    authFetch("/voucher/create", {
      method: "POST",
      body:   JSON.stringify(body),
    }),

  /** Fetches the full audit trail for a voucher (by Firestore document ID). */
  audit: (voucherId: string): Promise<{ auditLogs: unknown[] }> =>
    apiFetch(`/audit/${encodeURIComponent(voucherId)}`),
};

// ── Beneficiaries ─────────────────────────────────────────────────────────────

export interface ListBeneficiariesParams {
  campaignId?:  string;
  status?:      BeneficiaryStatus;
  limit?:       number;
  startAfter?:  string;
  orderBy?:     "createdAt" | "name" | "totalAmountRedeemed";
  dir?:         "asc" | "desc";
}

export interface CreateBeneficiaryBody {
  name:          string;
  idNumber?:     string | null;
  phone?:        string | null;
  email?:        string | null;
  campaignId?:   string | null;
  issuerId?:     string | null;
  metadata?:     Record<string, unknown> | null;
}

export interface UpdateBeneficiaryBody {
  name?:     string;
  phone?:    string | null;
  email?:    string | null;
  idNumber?: string | null;
  metadata?: Record<string, unknown> | null;
}

export const beneficiariesApi = {
  /** Lists beneficiaries, paginated with optional filters. */
  list: (
    params: ListBeneficiariesParams = {},
  ): Promise<{ beneficiaries: Beneficiary[]; hasMore: boolean; lastId: string | null; count: number }> =>
    apiFetch(`/beneficiaries${buildQuery(params)}`),

  /** Fetches a single beneficiary by Firestore document ID. */
  get: (id: string): Promise<{ beneficiary: Beneficiary }> =>
    apiFetch(`/beneficiaries/${id}`),

  /** Creates a new beneficiary, optionally enrolled in a campaign. */
  create: (
    body: CreateBeneficiaryBody,
  ): Promise<{ success: boolean; beneficiary: Beneficiary }> =>
    authFetch("/beneficiaries", {
      method: "POST",
      body:   JSON.stringify(body),
    }),

  /** Updates mutable fields on a beneficiary. */
  update: (
    id:   string,
    body: UpdateBeneficiaryBody,
  ): Promise<{ success: boolean }> =>
    authFetch(`/beneficiaries/${id}`, {
      method: "PATCH",
      body:   JSON.stringify(body),
    }),

  /** Suspends a beneficiary, preventing redemptions. */
  suspend: (
    id:      string,
    reason?: string,
  ): Promise<{ success: boolean }> =>
    authFetch(`/beneficiaries/${id}/suspend`, {
      method: "POST",
      body:   JSON.stringify({ reason: reason ?? null }),
    }),

  /** Reinstates a suspended beneficiary. */
  reinstate: (id: string): Promise<{ success: boolean }> =>
    authFetch(`/beneficiaries/${id}/reinstate`, { method: "POST" }),

  /** Lists all vouchers assigned to a beneficiary, paginated. */
  vouchers: (
    id:          string,
    limit?:      number,
    startAfter?: string,
  ): Promise<{ vouchers: Voucher[]; hasMore: boolean; lastId: string | null }> =>
    apiFetch(`/beneficiaries/${id}/vouchers${buildQuery({ limit, startAfter })}`),

  /**
   * Admin: sets or resets the beneficiary's PIN.
   * The PIN is a 4–6 digit code; it is hashed server-side and never stored
   * in plain text. The raw PIN is transmitted over HTTPS and must never be
   * logged on the client.
   */
  setPin: (
    id:  string,
    pin: string,
  ): Promise<{ success: boolean; message: string }> =>
    authFetch(`/beneficiaries/${id}/pin`, {
      method: "POST",
      body:   JSON.stringify({ pin }),
    }),

  /** Admin: removes the PIN from a beneficiary, disabling PIN-gated redemptions. */
  removePin: (id: string): Promise<{ success: boolean; message: string }> =>
    authFetch(`/beneficiaries/${id}/pin`, { method: "DELETE" }),

  /**
   * POS: validates the beneficiary's PIN and returns a short-lived token.
   * The token must be passed as `beneficiaryPinToken` in POST /api/redeem.
   * Tokens expire after 5 minutes and are single-use.
   */
  validatePin: (
    id:  string,
    pin: string,
  ): Promise<{ success: boolean; token: string; expiresAt: string; message: string }> =>
    apiFetch(`/beneficiaries/${id}/validate-pin`, {
      method: "POST",
      body:   JSON.stringify({ pin }),
    }),

  /**
   * Issues a new ORIGINAL voucher directly to a beneficiary.
   * Delegates to POST /api/voucher/create with the beneficiaryId pre-filled.
   */
  issueVoucher: (
    beneficiaryId: string,
    body: Omit<CreateVoucherBody, "beneficiaryId">,
  ): Promise<{ success: boolean; voucher: Voucher }> =>
    authFetch("/voucher/create", {
      method: "POST",
      body:   JSON.stringify({ ...body, beneficiaryId }),
    }),
};

// ── Analytics ─────────────────────────────────────────────────────────────────

export const analyticsApi = {
  /**
   * Returns the pre-computed summary document for a campaign.
   * This is the primary data source for all dashboard KPI cards.
   * O(1) Firestore read — single document.
   */
  summary: (campaignId: string): Promise<{ summary: CampaignAnalytics }> =>
    apiFetch(`/analytics/${encodeURIComponent(campaignId)}`),

  /**
   * Returns daily redemption snapshots for the last `days` calendar days.
   * Used for the redemption trend line chart.
   * At most `days` Firestore reads.
   */
  trend: (
    campaignId: string,
    days = 30,
  ): Promise<{ trend: DailySnapshot[]; days: number; campaignId: string }> =>
    apiFetch(`/analytics/${encodeURIComponent(campaignId)}/trend?days=${days}`),

  /**
   * Batch-fetches summary documents for multiple campaigns.
   * Used by the campaign list view to show KPIs in each row.
   */
  multiSummary: (
    campaignIds: string[],
  ): Promise<{ summaries: Record<string, CampaignAnalytics> }> =>
    apiFetch("/analytics/campaigns", {
      method: "POST",
      body:   JSON.stringify({ campaignIds }),
    }),

  /**
   * Admin: triggers a full recompute of the analytics document from scratch.
   * EXPENSIVE — scans vouchers, transactions, and beneficiaries collections.
   * Use only for initial backfill or reconciliation after counter drift.
   */
  recompute: (
    campaignId: string,
  ): Promise<{ success: boolean; summary: CampaignAnalytics }> =>
    authFetch(`/analytics/${encodeURIComponent(campaignId)}/recompute`, {
      method: "POST",
    }),
};

// ── Redemption ────────────────────────────────────────────────────────────────

export interface RedeemBody {
  voucherNumber:      string;
  merchantId?:        string;
  amount:             number;
  idempotencyKey?:    string;
  beneficiaryPinToken?: string;
}

export const redemptionApi = {
  /**
   * Processes a voucher redemption.
   * Delegates to the full Firestore transaction engine in redeemVoucher.js.
   * Pass `idempotencyKey` (a client-generated UUID) to enable at-most-once
   * semantics across network retries.
   */
  redeem: (body: RedeemBody): Promise<Record<string, unknown>> =>
    authFetch("/redeem", {
      method: "POST",
      body:   JSON.stringify(body),
    }),
};
